import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useState } from "react";
import { toast } from "sonner";
import { Zap, Plus, Trash2, ArrowRight, CheckCircle2, XCircle, Clock, Activity, KeyRound, Search, Pencil, Webhook, Copy } from "lucide-react";
import { toUserFriendlyError } from "@/lib/error-messages";

const TRIGGER_LABELS: Record<string, string> = {
  new_contact: "Novo contato cadastrado",
  tag_added: "Tag adicionada ao contato",
  tag_removed: "Tag removida do contato",
  keyword_received: "Palavra-chave recebida",
  inactivity: "Inatividade do contato",
  scheduled: "Agendamento periódico",
};

const ACTION_LABELS: Record<string, string> = {
  send_template: "Enviar template WhatsApp",
  add_tag: "Adicionar tag ao contato",
  remove_tag: "Remover tag do contato",
  update_field: "Atualizar campo do contato",
};

function StatusBadge({ status }: { status: string }) {
  if (status === "success") return (
    <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-200">
      <CheckCircle2 className="h-3 w-3" /> Sucesso
    </span>
  );
  if (status === "failed") return (
    <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full border border-red-200">
      <XCircle className="h-3 w-3" /> Falhou
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full border border-yellow-200">
      <Clock className="h-3 w-3" /> {status}
    </span>
  );
}

export default function Automations() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "",
    triggerType: "new_contact" as "new_contact" | "tag_added" | "tag_removed" | "keyword_received" | "inactivity" | "scheduled",
    actionType: "send_template" as "send_template" | "add_tag" | "remove_tag" | "update_field",
    templateId: "",
    triggerKeyword: "",
    inactivityDays: "7",
    triggerContactType: "" as "" | "public_admin" | "empresa" | "cliente_final",
    triggerState: "",
    updateField: "",
    updateValue: "",
  });
  const utils = trpc.useUtils();

  const { data: automations, isLoading } = trpc.automations.list.useQuery();
  const { data: templates } = trpc.templates.list.useQuery();
  const { data: logs, isLoading: logsLoading } = trpc.automationLogs.list.useQuery({ limit: 100 });

  useContextoDeLista({
    titulo: "Automações do WhatsApp",
    itens: (automations ?? []).map((a: any) => ({
      id: a.id, nome: a.name, ativa: a.isActive, gatilho: a.triggerType, acao: a.actionType,
    })),
    extra: { ultimasExecucoes: (logs ?? []).slice(0, 5).map((l: any) => ({ automacao: l.automationId, status: l.status, quando: l.createdAt })) },
  });


  // ── Keyword Groups ──────────────────────────────────────────────────────────
  const [showKeywordForm, setShowKeywordForm] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<any>(null);
  const [keywordSearch, setKeywordSearch] = useState("");
  const [kwForm, setKwForm] = useState({
    name: "",
    keywordsText: "",
    matchType: "contains" as "exact" | "contains" | "starts_with",
    flowId: "" as string,
    isActive: true,
  });

  const { data: keywordGroups, isLoading: kwLoading } = trpc.keywordGroups.list.useQuery();
  const { data: flowsList } = trpc.flows.list.useQuery();

  const kwCreateMut = trpc.keywordGroups.create.useMutation({
    onSuccess: () => {
      utils.keywordGroups.list.invalidate();
      toast.success("Grupo de palavras-chave criado!");
      closeKeywordForm();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const kwUpdateMut = trpc.keywordGroups.update.useMutation({
    onSuccess: () => {
      utils.keywordGroups.list.invalidate();
      toast.success("Grupo atualizado!");
      closeKeywordForm();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const kwDeleteMut = trpc.keywordGroups.delete.useMutation({
    onSuccess: () => { utils.keywordGroups.list.invalidate(); toast.success("Grupo removido!"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const kwToggleMut = trpc.keywordGroups.toggle.useMutation({
    onSuccess: () => utils.keywordGroups.list.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const closeKeywordForm = () => {
    setShowKeywordForm(false);
    setEditingKeyword(null);
    setKwForm({ name: "", keywordsText: "", matchType: "contains", flowId: "", isActive: true });
  };

  const openKeywordEdit = (kw: any) => {
    setEditingKeyword(kw);
    setKwForm({
      name: kw.name,
      keywordsText: (kw.keywords ?? []).join(", "),
      matchType: kw.matchType,
      flowId: kw.flowId ? String(kw.flowId) : "",
      isActive: kw.isActive,
    });
    setShowKeywordForm(true);
  };

  const handleKeywordSave = () => {
    if (!kwForm.name) { toast.error("Nome é obrigatório"); return; }
    const keywords = kwForm.keywordsText.split(",").map(k => k.trim()).filter(Boolean);
    if (keywords.length === 0) { toast.error("Adicione pelo menos uma palavra-chave"); return; }

    const payload = {
      name: kwForm.name,
      keywords,
      matchType: kwForm.matchType,
      flowId: kwForm.flowId ? Number(kwForm.flowId) : null,
      isActive: kwForm.isActive,
    };

    if (editingKeyword) {
      kwUpdateMut.mutate({ id: editingKeyword.id, ...payload });
    } else {
      kwCreateMut.mutate(payload);
    }
  };

  const MATCH_LABELS: Record<string, string> = {
    exact: "Exata",
    contains: "Contém",
    starts_with: "Começa com",
  };

  const filteredKeywordGroups = keywordGroups?.filter(
    (kw) => !keywordSearch || kw.name.toLowerCase().includes(keywordSearch.toLowerCase())
  );

  // ── Webhooks ───────────────────────────────────────────────────────────────
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<any>(null);
  const [webhookSearch, setWebhookSearch] = useState("");
  const [whForm, setWhForm] = useState({
    name: "",
    flowId: "" as string,
    isActive: true,
    fieldMappingText: "",
  });

  const { data: webhooksList, isLoading: whLoading } = trpc.webhooks.list.useQuery();

  const whCreateMut = trpc.webhooks.create.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Webhook criado!");
      closeWebhookForm();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const whUpdateMut = trpc.webhooks.update.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Webhook atualizado!");
      closeWebhookForm();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const whDeleteMut = trpc.webhooks.delete.useMutation({
    onSuccess: () => { utils.webhooks.list.invalidate(); toast.success("Webhook removido!"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const whToggleMut = trpc.webhooks.toggle.useMutation({
    onSuccess: () => utils.webhooks.list.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const closeWebhookForm = () => {
    setShowWebhookForm(false);
    setEditingWebhook(null);
    setWhForm({ name: "", flowId: "", isActive: true, fieldMappingText: "" });
  };

  const openWebhookEdit = (wh: any) => {
    setEditingWebhook(wh);
    const mapping = wh.fieldMapping ? Object.entries(wh.fieldMapping).map(([k, v]) => `${k}:${v}`).join(", ") : "";
    setWhForm({
      name: wh.name,
      flowId: wh.flowId ? String(wh.flowId) : "",
      isActive: wh.isActive,
      fieldMappingText: mapping,
    });
    setShowWebhookForm(true);
  };

  const handleWebhookSave = () => {
    if (!whForm.name) { toast.error("Nome é obrigatório"); return; }

    let fieldMapping: Record<string, string> | null = null;
    if (whForm.fieldMappingText.trim()) {
      fieldMapping = {};
      for (const pair of whForm.fieldMappingText.split(",")) {
        const [key, value] = pair.split(":").map(s => s.trim());
        if (key && value) fieldMapping[key] = value;
      }
    }

    const payload = {
      name: whForm.name,
      flowId: whForm.flowId ? Number(whForm.flowId) : null,
      isActive: whForm.isActive,
      fieldMapping,
    };

    if (editingWebhook) {
      whUpdateMut.mutate({ id: editingWebhook.id, ...payload });
    } else {
      whCreateMut.mutate(payload);
    }
  };

  const filteredWebhooks = webhooksList?.filter(
    (wh) => !webhookSearch || wh.name.toLowerCase().includes(webhookSearch.toLowerCase())
  );

  const totalWebhookRequests = webhooksList?.reduce((sum, wh) => sum + wh.requestCount, 0) ?? 0;
  const totalWebhookLimit = webhooksList?.reduce((sum, wh) => sum + wh.requestLimit, 0) || 25000;

  const copyWebhookUrl = (token: string) => {
    const url = `${window.location.origin}/api/webhooks/in/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copiada!");
  };

  const createMut = trpc.automations.create.useMutation({
    onSuccess: () => {
      utils.automations.list.invalidate();
      toast.success("Automação criada!");
      setShowForm(false);
      setForm({ name: "", description: "", triggerType: "new_contact", actionType: "send_template", templateId: "", triggerKeyword: "", inactivityDays: "7", triggerContactType: "", triggerState: "", updateField: "", updateValue: "" });
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const toggleMut = trpc.automations.toggleActive.useMutation({
    onSuccess: () => { utils.automations.list.invalidate(); utils.automationLogs.list.invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteMut = trpc.automations.delete.useMutation({
    onSuccess: () => { utils.automations.list.invalidate(); toast.success("Automação removida!"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleSave = () => {
    if (!form.name) { toast.error("Nome é obrigatório"); return; }
    let triggerConfig: Record<string, unknown> | undefined;
    if (form.triggerType === "keyword_received" && form.triggerKeyword) {
      triggerConfig = { keywords: form.triggerKeyword.split(",").map(k => k.trim().toLowerCase()).filter(Boolean) };
    } else if (form.triggerType === "inactivity") {
      triggerConfig = { inactivityDays: Number(form.inactivityDays) };
    } else if (form.triggerType === "new_contact") {
      const cfg: Record<string, unknown> = {};
      if (form.triggerContactType) cfg.contactType = form.triggerContactType;
      if (form.triggerState) cfg.state = form.triggerState;
      if (Object.keys(cfg).length > 0) triggerConfig = cfg;
    }
    let actionConfig: Record<string, unknown> | undefined;
    if (form.actionType === "update_field" && form.updateField) {
      actionConfig = { field: form.updateField, value: form.updateValue };
    }
    createMut.mutate({
      name: form.name,
      description: form.description || null,
      triggerType: form.triggerType,
      actionType: form.actionType,
      templateId: form.templateId ? Number(form.templateId) : null,
      triggerConfig,
      actionConfig,
    });
  };

  const activeCount = automations?.filter(a => a.isActive).length ?? 0;
  const totalLogs = logs?.length ?? 0;
  const successLogs = logs?.filter(l => l.status === "success").length ?? 0;

  return (
    <PermissionGuard permission="whatsapp:manage_automations">
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Automações</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Fluxos automáticos baseados em gatilhos de eventos
            </p>
          </div>
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Nova Automação
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{automations?.length ?? 0}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{activeCount}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Ativas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{totalLogs}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Execuções</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="automations">
          <TabsList>
            <TabsTrigger value="automations" className="gap-1.5">
              <Zap className="h-3.5 w-3.5" /> Automações
              {(automations?.length ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">{automations?.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="keywords" className="gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Palavras-Chave
              {(keywordGroups?.length ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">{keywordGroups?.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="gap-1.5">
              <Webhook className="h-3.5 w-3.5" /> Webhooks
              {(webhooksList?.length ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">{webhooksList?.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="logs" className="gap-1.5">
              <Activity className="h-3.5 w-3.5" /> Histórico de Execuções
              {totalLogs > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">{totalLogs}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Lista de Automações */}
          <TabsContent value="automations" className="mt-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />)}
              </div>
            ) : (automations?.length ?? 0) === 0 ? (
              <div className="text-center py-16">
                <Zap className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground font-medium">Nenhuma automação criada</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Crie automações para enviar mensagens automaticamente baseadas em eventos
                </p>
                <Button size="sm" className="mt-4" onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Criar primeira automação
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {automations?.map((a) => (
                  <Card key={a.id} className={`transition-all ${a.isActive ? "border-primary/30 bg-primary/5" : ""}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{a.name}</p>
                            {a.isActive ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200">Ativa</span>
                            ) : (
                              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full border">Inativa</span>
                            )}
                          </div>
                          {a.description && <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>}
                          <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
                            <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                              {TRIGGER_LABELS[a.triggerType] ?? a.triggerType}
                            </span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200">
                              {ACTION_LABELS[a.actionType] ?? a.actionType}
                            </span>
                          </div>
                          {(a.triggeredCount ?? 0) > 0 && (
                            <p className="text-xs text-muted-foreground mt-1.5">
                              Executada <strong>{a.triggeredCount}</strong> vez(es)
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Switch
                            checked={a.isActive}
                            onCheckedChange={(checked) => toggleMut.mutate({ id: a.id, isActive: checked })}
                          />
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => { if (confirm("Remover esta automação?")) deleteMut.mutate({ id: a.id }); }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Palavras-Chave */}
          <TabsContent value="keywords" className="mt-4">
            <div className="space-y-3">
              {/* Barra de ações */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome..."
                    value={keywordSearch}
                    onChange={(e) => setKeywordSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button size="sm" onClick={() => setShowKeywordForm(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Criar
                </Button>
              </div>

              {kwLoading ? (
                <div className="space-y-3">
                  {[1, 2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />)}
                </div>
              ) : (filteredKeywordGroups?.length ?? 0) === 0 ? (
                <div className="text-center py-16">
                  <KeyRound className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground font-medium">Nenhum grupo de palavras-chave</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Crie grupos de palavras-chave para acionar fluxos automaticamente quando uma mensagem corresponder
                  </p>
                  <Button size="sm" className="mt-4" onClick={() => setShowKeywordForm(true)}>
                    <Plus className="h-4 w-4 mr-1.5" /> Criar primeiro grupo
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredKeywordGroups?.map((kw) => {
                    const flow = flowsList?.find((f: any) => f.id === kw.flowId);
                    return (
                      <Card key={kw.id} className={`transition-all ${kw.isActive ? "border-primary/30 bg-primary/5" : ""}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold">{kw.name}</p>
                                {kw.isActive ? (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200">Ativo</span>
                                ) : (
                                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full border">Inativo</span>
                                )}
                                <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200">
                                  {MATCH_LABELS[kw.matchType] ?? kw.matchType}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                {(kw.keywords as string[]).map((word, i) => (
                                  <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                    {word}
                                  </span>
                                ))}
                              </div>
                              {flow && (
                                <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                                  <ArrowRight className="h-3 w-3" />
                                  <span>Fluxo: <strong className="text-foreground">{flow.name}</strong></span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7"
                                onClick={() => openKeywordEdit(kw)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Switch
                                checked={kw.isActive}
                                onCheckedChange={(checked) => kwToggleMut.mutate({ id: kw.id, isActive: checked })}
                              />
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => { if (confirm("Remover este grupo de palavras-chave?")) kwDeleteMut.mutate({ id: kw.id }); }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Webhooks */}
          <TabsContent value="webhooks" className="mt-4">
            <div className="space-y-3">
              {/* Indicador de limite + ações */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome..."
                    value={webhookSearch}
                    onChange={(e) => setWebhookSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Requisições: {totalWebhookRequests.toLocaleString()}/{totalWebhookLimit.toLocaleString()}
                </span>
                <Button size="sm" onClick={() => setShowWebhookForm(true)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Criar
                </Button>
              </div>

              {whLoading ? (
                <div className="space-y-3">
                  {[1, 2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />)}
                </div>
              ) : (filteredWebhooks?.length ?? 0) === 0 ? (
                <div className="text-center py-16">
                  <Webhook className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground font-medium">Nenhum webhook configurado</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Crie webhooks para receber dados de sistemas externos e acionar fluxos automaticamente
                  </p>
                  <Button size="sm" className="mt-4" onClick={() => setShowWebhookForm(true)}>
                    <Plus className="h-4 w-4 mr-1.5" /> Criar primeiro webhook
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredWebhooks?.map((wh) => {
                    const flow = flowsList?.find((f: any) => f.id === wh.flowId);
                    const usagePercent = wh.requestLimit > 0 ? Math.round((wh.requestCount / wh.requestLimit) * 100) : 0;
                    return (
                      <Card key={wh.id} className={`transition-all ${wh.isActive ? "border-primary/30 bg-primary/5" : ""}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold">{wh.name}</p>
                                {wh.isActive ? (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200">Ativo</span>
                                ) : (
                                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full border">Inativo</span>
                                )}
                                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                  {wh.requestCount.toLocaleString()} req ({usagePercent}%)
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-2">
                                <code className="text-xs bg-muted px-2 py-1 rounded font-mono truncate max-w-xs">
                                  /api/webhooks/in/{wh.urlToken.slice(0, 12)}...
                                </code>
                                <Button
                                  variant="ghost" size="icon" className="h-6 w-6"
                                  onClick={() => copyWebhookUrl(wh.urlToken)}
                                  title="Copiar URL"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              {flow && (
                                <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                                  <ArrowRight className="h-3 w-3" />
                                  <span>Fluxo: <strong className="text-foreground">{flow.name}</strong></span>
                                </div>
                              )}
                              {wh.fieldMapping && Object.keys(wh.fieldMapping).length > 0 && (
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  {Object.entries(wh.fieldMapping as Record<string, string>).map(([key, val]) => (
                                    <span key={key} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200">
                                      {key} → {val}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7"
                                onClick={() => openWebhookEdit(wh)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Switch
                                checked={wh.isActive}
                                onCheckedChange={(checked) => whToggleMut.mutate({ id: wh.id, isActive: checked })}
                              />
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => { if (confirm("Remover este webhook?")) whDeleteMut.mutate({ id: wh.id }); }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Histórico de Execuções */}
          <TabsContent value="logs" className="mt-4">
            {logsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}
              </div>
            ) : (logs?.length ?? 0) === 0 ? (
              <div className="text-center py-16">
                <Activity className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground font-medium">Nenhuma execução registrada</p>
                <p className="text-sm text-muted-foreground mt-1">
                  As execuções das automações aparecerão aqui quando forem disparadas
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Resumo */}
                <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3 p-3 bg-muted/50 rounded-lg">
                  <span><strong className="text-foreground">{totalLogs}</strong> execuções</span>
                  <span>·</span>
                  <span><strong className="text-green-600">{successLogs}</strong> com sucesso</span>
                  <span>·</span>
                  <span><strong className="text-red-600">{totalLogs - successLogs}</strong> com falha</span>
                </div>

                {logs?.map((log) => {
                  const automation = automations?.find(a => a.id === log.automationId);
                  return (
                    <div key={log.id} className="flex items-start gap-3 p-3 border rounded-lg bg-card hover:bg-muted/30 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium truncate">
                            {automation?.name ?? `Automação #${log.automationId}`}
                          </p>
                          <StatusBadge status={log.status} />
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                          {log.contactName && (
                            <span>Contato: <strong className="text-foreground">{log.contactName}</strong></span>
                          )}
                          {log.contactPhone && (
                            <span>{log.contactPhone}</span>
                          )}
                          {log.details && (
                            <span className="text-muted-foreground/70 truncate max-w-xs">{log.details}</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground shrink-0 mt-0.5">
                        {log.createdAt ? new Date(log.createdAt).toLocaleString("pt-BR") : "—"}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog de criação */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) setShowForm(false); }}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>Nova Automação</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nome *</Label>
              <Input placeholder="Ex: Boas-vindas para novos contatos" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input placeholder="Descreva o objetivo desta automação" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Gatilho (Quando?)</Label>
              <Select value={form.triggerType} onValueChange={(v) => setForm(f => ({ ...f, triggerType: v as any }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TRIGGER_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(form.triggerType as string) === "keyword_received" && (
              <div>
                <Label>Palavra-chave</Label>
                <Input placeholder="Ex: oi, olá, info" value={form.triggerKeyword} onChange={(e) => setForm(f => ({ ...f, triggerKeyword: e.target.value }))} className="mt-1" />
                <p className="text-xs text-muted-foreground mt-1">Separe múltiplas palavras por vírgula</p>
              </div>
            )}

            {(form.triggerType as string) === "inactivity" && (
              <div>
                <Label>Dias de inatividade</Label>
                <Input type="number" min="1" max="365" placeholder="7" value={form.inactivityDays} onChange={(e) => setForm(f => ({ ...f, inactivityDays: e.target.value }))} className="mt-1" />
              </div>
            )}

            {(form.triggerType as string) === "new_contact" && (
              <>
                <div>
                  <Label>Filtrar por tipo <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                  <Select value={form.triggerContactType || "_all"} onValueChange={(v) => setForm(f => ({ ...f, triggerContactType: (v === "_all" ? "" : v) as any }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Todos</SelectItem>
                      <SelectItem value="public_admin">Administração Pública</SelectItem>
                      <SelectItem value="empresa">Empresa</SelectItem>
                      <SelectItem value="cliente_final">Cliente Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Filtrar por estado <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                  <Input placeholder="Ex: SP, RJ, MG" value={form.triggerState} onChange={(e) => setForm(f => ({ ...f, triggerState: e.target.value }))} className="mt-1" />
                </div>
              </>
            )}

            <div>
              <Label>Ação (O quê?)</Label>
              <Select value={form.actionType} onValueChange={(v) => setForm(f => ({ ...f, actionType: v as any }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTION_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.actionType === "send_template" && (
              <div>
                <Label>Template a enviar</Label>
                <Select value={form.templateId} onValueChange={(v) => setForm(f => ({ ...f, templateId: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar template..." /></SelectTrigger>
                  <SelectContent>
                    {templates?.map(t => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.actionType === "update_field" && (
              <>
                <div>
                  <Label>Campo</Label>
                  <Select value={form.updateField} onValueChange={(v) => setForm(f => ({ ...f, updateField: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar campo..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notes">Notas</SelectItem>
                      <SelectItem value="companyName">Empresa</SelectItem>
                      <SelectItem value="city">Cidade</SelectItem>
                      <SelectItem value="state">Estado</SelectItem>
                      <SelectItem value="companyType">Tipo de empresa</SelectItem>
                      <SelectItem value="sector">Setor</SelectItem>
                      <SelectItem value="contactType">Tipo de contato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor</Label>
                  <Input placeholder="Novo valor para o campo" value={form.updateValue} onChange={(e) => setForm(f => ({ ...f, updateValue: e.target.value }))} className="mt-1" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={createMut.isPending}>
              {createMut.isPending ? "Criando..." : "Criar Automação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog de criação/edição de Palavras-Chave */}
      <Dialog open={showKeywordForm} onOpenChange={(o) => { if (!o) closeKeywordForm(); }}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>{editingKeyword ? "Editar" : "Novo"} Grupo de Palavras-Chave</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nome do grupo *</Label>
              <Input
                placeholder="Ex: Saudações, Preços, Suporte"
                value={kwForm.name}
                onChange={(e) => setKwForm(f => ({ ...f, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Palavras-chave *</Label>
              <Input
                placeholder="oi, olá, bom dia, hey"
                value={kwForm.keywordsText}
                onChange={(e) => setKwForm(f => ({ ...f, keywordsText: e.target.value }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Separe múltiplas palavras por vírgula</p>
            </div>
            <div>
              <Label>Tipo de correspondência</Label>
              <Select value={kwForm.matchType} onValueChange={(v) => setKwForm(f => ({ ...f, matchType: v as any }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exata — mensagem deve ser igual</SelectItem>
                  <SelectItem value="contains">Contém — mensagem contém a palavra</SelectItem>
                  <SelectItem value="starts_with">Começa com — mensagem inicia com a palavra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fluxo a acionar</Label>
              <Select value={kwForm.flowId || "_none"} onValueChange={(v) => setKwForm(f => ({ ...f, flowId: v === "_none" ? "" : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar fluxo..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nenhum</SelectItem>
                  {flowsList?.map((f: any) => (
                    <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={kwForm.isActive}
                onCheckedChange={(checked) => setKwForm(f => ({ ...f, isActive: checked }))}
              />
              <Label className="cursor-pointer">Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeKeywordForm}>Cancelar</Button>
            <Button onClick={handleKeywordSave} disabled={kwCreateMut.isPending || kwUpdateMut.isPending}>
              {(kwCreateMut.isPending || kwUpdateMut.isPending) ? "Salvando..." : editingKeyword ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog de criação/edição de Webhook */}
      <Dialog open={showWebhookForm} onOpenChange={(o) => { if (!o) closeWebhookForm(); }}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>{editingWebhook ? "Editar" : "Novo"} Webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nome *</Label>
              <Input
                placeholder="Ex: Integração Hotmart, Webhook Stripe"
                value={whForm.name}
                onChange={(e) => setWhForm(f => ({ ...f, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            {editingWebhook && (
              <div>
                <Label>URL do Webhook</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    readOnly
                    value={`${window.location.origin}/api/webhooks/in/${editingWebhook.urlToken}`}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline" size="icon" className="shrink-0"
                    onClick={() => copyWebhookUrl(editingWebhook.urlToken)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Envie requisições POST para esta URL</p>
              </div>
            )}
            <div>
              <Label>Fluxo a acionar</Label>
              <Select value={whForm.flowId || "_none"} onValueChange={(v) => setWhForm(f => ({ ...f, flowId: v === "_none" ? "" : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar fluxo..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nenhum</SelectItem>
                  {flowsList?.map((f: any) => (
                    <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mapeamento de campos</Label>
              <Input
                placeholder="payload_email:email, payload_nome:name"
                value={whForm.fieldMappingText}
                onChange={(e) => setWhForm(f => ({ ...f, fieldMappingText: e.target.value }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Formato: campo_payload:campo_contato, separados por vírgula</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={whForm.isActive}
                onCheckedChange={(checked) => setWhForm(f => ({ ...f, isActive: checked }))}
              />
              <Label className="cursor-pointer">Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeWebhookForm}>Cancelar</Button>
            <Button onClick={handleWebhookSave} disabled={whCreateMut.isPending || whUpdateMut.isPending}>
              {(whCreateMut.isPending || whUpdateMut.isPending) ? "Salvando..." : editingWebhook ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </PermissionGuard>
  );
}
